import { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocLinks } from "@/components/PracticalDocs";
import { api, errMsg } from "@/lib/api";

const ATTACH_TYPES = [
  "Equipment manual",
  "Chemical safety",
  "Risk assessment",
  "Instructions",
  "Other",
];

function ExistingInventoryDocs({ inventoryId, linked, onDone }) {
  const [query, setQuery] = useState("");
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .get("/documents", { params: { q: query } })
        .then((response) => setDocuments(response.data.documents))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const link = async (document) => {
    try {
      const { data } = await api.post(`/documents/${document.id}/inventory`, {
        inventory_id: inventoryId,
      });
      toast.success(`Attached ${document.title}`);
      onDone(data);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const linkedIds = new Set(linked.map((document) => document.id));
  return (
    <div className="min-w-0 space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search resources…"
          className="pl-9"
          data-testid="inventory-attach-doc-search"
        />
      </div>
      <ul className="max-h-72 w-full min-w-0 divide-y overflow-y-auto border">
        {documents.length === 0 && (
          <li className="p-3 text-sm text-muted-foreground">
            No resources found. Upload one instead.
          </li>
        )}
        {documents.map((document) => (
          <li
            key={document.id}
            className="flex min-w-0 items-center justify-between gap-3 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{document.title}</div>
              <div className="text-xs text-muted-foreground">{document.category}</div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              disabled={linkedIds.has(document.id)}
              onClick={() => link(document)}
              data-testid={`inventory-attach-doc-link-${document.id}`}
            >
              {linkedIds.has(document.id) ? "Attached" : "Attach"}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UploadInventoryDocument({ inventoryId, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: "",
    category: "Equipment manual",
    visibility: "all",
  });
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    setBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("inventory_id", inventoryId);
      Object.entries(form).forEach(([key, value]) => data.append(key, value));
      await api.post("/documents", data);
      const response = await api.get(`/documents/for-inventory/${inventoryId}`);
      toast.success("Uploaded and attached");
      onDone(response.data);
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.webp"
        onChange={(event) => {
          const nextFile = event.target.files?.[0];
          setFile(nextFile || null);
          if (nextFile && !form.title) {
            setForm((current) => ({
              ...current,
              title: nextFile.name.replace(/\.[^.]+$/, ""),
            }));
          }
        }}
        data-testid="inventory-attach-upload-file-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full border-2 border-dashed p-5 text-sm text-muted-foreground hover:border-primary/60"
        data-testid="inventory-attach-upload-file-button"
      >
        {file ? (
          <span className="font-medium text-foreground">{file.name}</span>
        ) : (
          "Choose a PDF, Word, Excel, text file or image (max 15 MB)"
        )}
      </button>
      <div className="space-y-1">
        <Label>Title</Label>
        <Input
          value={form.title}
          onChange={(event) => setForm({ ...form, title: event.target.value })}
          data-testid="inventory-attach-upload-title"
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Type</Label>
          <Select
            value={form.category}
            onValueChange={(value) => setForm({ ...form, category: value })}
          >
            <SelectTrigger data-testid="inventory-attach-upload-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ATTACH_TYPES.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Who can see it</Label>
          <Select
            value={form.visibility}
            onValueChange={(value) => setForm({ ...form, visibility: value })}
          >
            <SelectTrigger data-testid="inventory-attach-upload-visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              <SelectItem value="prep">Technicians and admins only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button
        onClick={upload}
        disabled={busy || !file || !form.title.trim()}
        className="w-full"
        data-testid="inventory-attach-upload-save"
      >
        <Upload className="mr-2 h-4 w-4" />
        {busy ? "Uploading…" : "Upload and attach"}
      </Button>
    </div>
  );
}

export function InventoryDocs({ inventoryId, canEdit }) {
  const [documents, setDocuments] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("existing");
  const load = useCallback(() => {
    return api
      .get(`/documents/for-inventory/${inventoryId}`)
      .then((response) => setDocuments(response.data))
      .catch(() => {});
  }, [inventoryId]);

  useEffect(() => {
    load();
  }, [load]);

  const unlink = async (document) => {
    try {
      const { data } = await api.delete(
        `/documents/${document.id}/inventory/${inventoryId}`,
      );
      setDocuments(data);
      toast.success(`Removed ${document.title} from this item`);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  if (!canEdit && !documents.length) {
    return null;
  }

  return (
    <div data-testid="inventory-docs-section">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold">Documents & manuals</h4>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setTab("existing");
              setOpen(true);
            }}
            data-testid="inventory-attach-doc-button"
          >
            <Paperclip className="mr-1.5 h-3.5 w-3.5" />
            Attach
          </Button>
        )}
      </div>
      {documents.length ? (
        <DocLinks
          docs={documents}
          onUnlink={canEdit ? unlink : null}
          testId="inventory-docs"
        />
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="inventory-docs-empty">
          No documents attached yet.
        </p>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-lg overflow-hidden"
          data-testid="inventory-attach-doc-dialog"
        >
          <DialogHeader>
            <DialogTitle>Attach a document</DialogTitle>
          </DialogHeader>
          <Tabs value={tab} onValueChange={setTab} className="min-w-0">
            <TabsList className="grid w-full min-w-0 grid-cols-2">
              <TabsTrigger
                className="min-w-0 truncate"
                value="existing"
                data-testid="inventory-attach-tab-existing"
              >
                From resources
              </TabsTrigger>
              <TabsTrigger
                className="min-w-0 truncate"
                value="upload"
                data-testid="inventory-attach-tab-upload"
              >
                Upload new
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {tab === "existing" ? (
            <ExistingInventoryDocs
              inventoryId={inventoryId}
              linked={documents}
              onDone={(data) => {
                setDocuments(data);
                setOpen(false);
              }}
            />
          ) : (
            <UploadInventoryDocument
              inventoryId={inventoryId}
              onDone={(data) => {
                setDocuments(data);
                setOpen(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}